import React, { useState } from 'react';
import { FaUser, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { FaEnvelope } from 'react-icons/fa6';
import { useNavigate, Link } from 'react-router-dom';
import { postData, getData } from '../../../webService/webservice.js';
import { toast } from 'react-toastify';
import '../../../styles/Auth/login.css';

const LoginPage = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(true);
    const [backgroundImages, setBackgroundImages] = useState([]);
    const [bgIndex, setBgIndex] = useState(0);

    React.useEffect(() => {
        const fetchBackgrounds = async () => {
            try {
                const response = await getData('appearance/backgrounds');
                if (response?.data?.results) {
                    const images = response.data.results
                        .filter(bg => bg?.uri)
                        .map(bg => {
                            const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, "");
                            const uri = bg.uri.replace(/^\/?/, "/");
                            return `${baseUrl}${uri}`;
                        });

                    console.log("Background Images:", images);
                    setBackgroundImages(images);
                }
            } catch (error) {
                console.error("Failed to load backgrounds:", error);
            }
        };
        fetchBackgrounds();
    }, []);

    React.useEffect(() => {
        if (backgroundImages.length === 0) return;
        const interval = setInterval(() => {
            setBgIndex((prev) => (prev + 1) % backgroundImages.length);
        }, 6000);
        return () => clearInterval(interval);
    }, [backgroundImages]);

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!acceptTerms) {
            toast.error("Please accept the Terms & Conditions and Privacy Policy to proceed.");
            return;
        }

        setIsLoading(true);

        try {
            const result = await postData("users/signin", { email, password });

            if (result?.success === false) {
                throw new Error(result.message || "Login failed.");
            }

            const userData = result?.data;

            if (!userData?.sessionToken) {
                throw new Error("No session token received.");
            }

            const setStorage = (key, value) => {
                sessionStorage.setItem(key, value);
                localStorage.setItem(key, value);
            };

            setStorage("Token", userData.sessionToken);
            setStorage("userId", userData._id);
            setStorage("name", userData.name);
            setStorage("role", userData.role);
            setStorage("email", userData.email);
            setStorage("hasPosAccess", userData.hasPosAccess);

            let storeIds = [];
            let stores = [];

            if (userData.role === "SuperAdmin" || userData.role === "SelfAdmin") {
                stores = userData.stores || (Array.isArray(userData.storeIds) && userData.storeIds.length > 0 ? userData.storeIds : []);
                if (stores.length === 0) {
                    try {
                        const storesRes = await getData("stores/stores?page=1&limit=20", {}, userData.sessionToken);
                        if (storesRes?.data?.results?.length > 0) {
                            stores = storesRes.data.results;
                        }
                    } catch (e) {
                        console.error("Failed to load stores during login", e);
                    }
                }
                storeIds = stores.map(s => (s?._id ? s._id : s));
            } else if (userData.role === "FranchiseAdmin") {
                stores = userData?.franchiseId?.stores || [];
                storeIds = stores.map(s => (s?._id ? s._id : s));
            } else if (
                userData.role === "ClusterManager" ||
                userData.role === "Biller" ||
                userData.role === "StoreAdmin" ||
                userData.role === "StoreManager" ||
                userData.role === "Accountant" ||
                userData.role === "Manager"
            ) {
                stores = userData.storeIds || [];
                storeIds = stores.map(s => (s?._id ? s._id : s));
            } else if (userData.role === "StoreCreator") {
                stores = [];
                storeIds = [];
            }

            setStorage("storeIds", JSON.stringify(storeIds));
            setStorage("stores", JSON.stringify(stores));

            if (storeIds.length > 0) {
                setStorage("currentStoreId", storeIds[0]);
            } else {
                localStorage.removeItem("currentStoreId");
                sessionStorage.removeItem("currentStoreId");
            }

            if (userData.role === "Biller") {
                if (userData.hasPosAccess === false) {
                    navigate("/appointments");
                } else {
                    navigate("/pos");
                }
            } else if (userData.role === "StoreCreator") {
                navigate("/stores");
            } else if (userData.role === "Accountant") {
                navigate("/sales");
            } else {
                navigate("/dashboard");
            }

            toast.success("Login successful!");
        } catch (error) {
            console.error("Login Error:", error);
            toast.error(error.message || "Login failed.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='login-bg'>
            <div className="login-bg-overlay"></div>
            {backgroundImages.map((img, index) => (
                <div
                    key={index}
                    className={`login-bg-img ${index === bgIndex ? 'active' : ''}`}
                    style={{
                        backgroundImage: `url("${img}")`
                    }}
                />
            ))}

            <div className='login-container'>
                <div className='login-card'>
                    <form className='login-form' onSubmit={handleLogin}>
                        <div className='login-logo'>
                            <img className='logo' src="/assets/logo.png" alt='logo' />
                            <p className='login-title'>Sign in to your account</p>
                        </div>

                        <div className='login-input-group'>
                            <FaEnvelope className='login-input-icon' />
                            <input
                                type='email'
                                placeholder='Enter your email Id'
                                required
                                className='login-input'
                                value={email}
                                onChange={e => setEmail(e.target.value.toLowerCase())}
                            />
                        </div>

                        <div className="login-input-group">
                            <FaLock className="login-input-icon" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Password"
                                className="login-input"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                            />
                            <span className="login-show" onClick={() => setShowPassword(s => !s)}>
                                {showPassword ? <FaEyeSlash className="login-eye-icon" /> : <FaEye className="login-eye-icon" />}
                            </span>
                        </div>

                        <div className="login-options">
                            <label className="login-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={e => setRememberMe(e.target.checked)}
                                    className="login-checkbox"
                                />
                                Remember me
                            </label>
                            <span
                                className="login-link"
                                onClick={() => navigate('/forgot-password')}
                            >
                                Forgot Password?
                            </span>
                        </div>

                        <div className="login-terms-acceptance">
                            <label className="login-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={acceptTerms}
                                    onChange={e => setAcceptTerms(e.target.checked)}
                                    className="login-checkbox"
                                    required
                                />
                                <span>I accept the&nbsp;</span>
                            </label>
                            <Link
                                to="/terms-and-conditions"
                                className="login-legal-link"
                                onClick={(e) => e.stopPropagation()}
                            >
                                Terms &amp; Conditions
                            </Link>
                            <span>&nbsp;and&nbsp;</span>
                            <Link
                                to="/privacy-policy"
                                className="login-legal-link"
                                onClick={(e) => e.stopPropagation()}
                            >
                                Privacy Policy
                            </Link>
                        </div>

                        <div className='sign-button'>
                            <button
                                type="submit"
                                className="login-btn-main"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Signing in...' : 'Sign in'}
                            </button>
                        </div>
                    </form>

                </div>
            </div>
        </div>
    );
};

export default LoginPage;
